export type AppRole =
  | "admin"
  | "finance"
  | "finance_admin"
  | "treasurer"
  | "bookkeeper"
  | "ap_clerk"
  | "ar_clerk"
  | "approver"
  | "pastor"
  | "youth"
  | "volunteer_coordinator"
  | "facilities"
  | "volunteer"
  | "auditor"
  | "staff"
  | "ministry_leader"
  | "member"
  | "super_admin"
  | "platform_super_admin"
  | "platform_support"
  | "platform_readonly";

export const PLATFORM_ROLES: AppRole[] = [
  "platform_super_admin",
  "platform_support",
  "platform_readonly",
];

// People-record write RLS = admin/staff/pastor (+platform). Shared by every page
// that gates a People-data write affordance, so the client-side check can't drift
// narrower than the server-side policy on one page but not another (#4592).
export const PEOPLE_WRITE_ROLES: AppRole[] = [
  "admin",
  "staff",
  "pastor",
  "platform_super_admin",
  "platform_support",
];

// households_update_authenticated additionally authorizes finance through
// is_staff_member() (which covers many more roles than this list — auditor,
// ministry_leader, etc. — but this stays a deliberate subset of that broader
// RLS grant, not an exact mirror of it, so the UI never offers an edit action
// RLS would then reject). Household-record fields (address, family group
// assignment) are a different write surface than People or giving units —
// keep this separate from PEOPLE_WRITE_ROLES and GIVING_UNIT_WRITE_ROLES (#4950).
// is_readonly_app_user_for_org (supabase/migrations/20260803222239_readonly_write_authority.sql)
// backs the restrictive readonly_roles_block_* policies: these roles can see but never write,
// even where a permissive staff policy (is_staff_member) would otherwise admit them.
// platform_readonly is global and blocks in every organization; auditor blocks in its own.
export const READONLY_APP_ROLES: AppRole[] = ["auditor", "platform_readonly"];

// create_or_reuse_family_group_for_household (supabase/migrations/
// 20260718160000_atomic_family_group_household_link.sql) is how FamilyGroupFormDialog creates a
// group, and it authorizes exactly these roles via has_role_in_org — narrower than the
// family_groups INSERT policy's is_staff_member() (STAFF_APP_ROLES), which admits auditors,
// bookkeepers and others the RPC then rejects with 42501.
export const FAMILY_GROUP_CREATE_ROLES: AppRole[] = [
  "admin",
  "staff",
  "pastor",
  "finance",
  "super_admin",
  "platform_super_admin",
  "platform_support",
];

export const HOUSEHOLD_WRITE_ROLES: AppRole[] = [
  ...PEOPLE_WRITE_ROLES,
  "finance",
];

// merge_household_records and get_household_role_anomalies RPCs
// (supabase/migrations/20260707193000_block_primary_role_household_merge.sql,
// 20260708064000_household_role_anomalies_rpc.sql) authorize exactly
// admin/staff/pastor — narrower than PEOPLE_WRITE_ROLES, which also includes
// platform_super_admin/platform_support for ordinary People writes. These two
// RPCs don't go through people_update_authenticated, so a platform-only viewer
// gated on PEOPLE_WRITE_ROLES sees a Merge action and an anomaly-diagnostic
// query that both 42501 (#4950 round 2).
export const HOUSEHOLD_MERGE_ROLES: AppRole[] = [
  "admin",
  "staff",
  "pastor",
];

// transition_household_primary_contact RPC
// (supabase/migrations/20260712008000_support_atomic_household_contact_detach.sql:32-50)
// authorizes admin/staff/pastor/finance — not the platform roles PEOPLE_WRITE_ROLES
// grants for ordinary People writes. handleMakePrimaryContact/handleClearPrimaryContact
// always call this RPC, and useMovePerson calls it conditionally whenever the moved
// member is the household's current primary contact — reusing PEOPLE_WRITE_ROLES let a
// platform-only viewer see primary-contact controls that 42501, while hiding them from
// finance, who is authorized (#4950 round 3). This currently has the same value as
// GIVING_UNIT_WRITE_ROLES by coincidence — they gate two different RPCs/policies that
// can diverge independently, so keep them as separate constants.
export const PRIMARY_CONTACT_ROLES: AppRole[] = [
  "admin",
  "staff",
  "pastor",
  "finance",
];

// giving_units insert/update/delete RLS authorizes exactly admin/staff/pastor/finance
// — notably NOT platform_super_admin/platform_support, unlike household and People
// writes. Keep this an exact mirror of that policy (#4950).
export const GIVING_UNIT_WRITE_ROLES: AppRole[] = [
  "admin",
  "staff",
  "pastor",
  "finance",
];

// Mirrors log_coordinator_volunteer_hours' management role array exactly,
// INCLUDING super_admin/platform_super_admin (issue #5006).
// Round-4 fix dropped those two from this list and OR'd in isPlatformSuperAdmin
// instead, reasoning they were global-only platform roles -- wrong:
// user_roles supports org-scoped super_admin grants too, and
// The RPC's has_role_in_org check only honors an org-scoped-or-null-org
// row for ANY role in its array, super_admin included. isPlatformSuperAdmin's
// hasAnyRole is fully global, so a user with super_admin scoped to a
// DIFFERENT org (not this one, not null-org) would incorrectly pass that
// check while RLS itself would reject them (round-5 Codex finding). Check
// the full role array with hasAnyOrgRole, not hasAnyRole -- a role held only
// in a different org must not grant access to this org's volunteer-hours
// data (PR #5002 rounds 4-5).
export const HOURS_MANAGEMENT_ROLES: AppRole[] = [
  "admin",
  "pastor",
  "staff",
  "ministry_leader",
  "volunteer_coordinator",
  "super_admin",
  "platform_super_admin",
];

export const STAFF_APP_ROLES: AppRole[] = [
  "admin",
  "finance",
  "finance_admin",
  "treasurer",
  "bookkeeper",
  "ap_clerk",
  "ar_clerk",
  "approver",
  "pastor",
  "youth",
  "volunteer_coordinator",
  "facilities",
  "auditor",
  "staff",
  "ministry_leader",
  "super_admin",
  "platform_super_admin",
  "platform_support",
  "platform_readonly",
];

const ALL_APP_ROLES: AppRole[] = [
  "admin",
  "finance",
  "finance_admin",
  "treasurer",
  "bookkeeper",
  "ap_clerk",
  "ar_clerk",
  "approver",
  "pastor",
  "youth",
  "volunteer_coordinator",
  "facilities",
  "volunteer",
  "auditor",
  "staff",
  "ministry_leader",
  "member",
  "super_admin",
  "platform_super_admin",
  "platform_support",
  "platform_readonly",
];

const APP_ROLE_SET = new Set<AppRole>(ALL_APP_ROLES);

export const normalizeRoles = (roles: string[]): AppRole[] =>
  roles.filter((role): role is AppRole => APP_ROLE_SET.has(role as AppRole));

const extractRoleList = (value: unknown): string[] => {
  if (typeof value === "string") {
    return [value];
  }

  if (Array.isArray(value)) {
    return value.filter((item): item is string => typeof item === "string");
  }

  return [];
};

export const collectRolesFromMetadata = (metadata?: Record<string, unknown>): string[] => {
  if (!metadata) return [];

  const sources = [
    metadata.role,
    metadata.roles,
    metadata.app_roles,
    metadata.app_role,
    metadata.platform_role,
    metadata.platform_roles,
    metadata.claims,
    metadata.custom_claims,
  ];

  const nestedSources = sources
    .filter((value): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value))
    .flatMap((value) => [
      value.role,
      value.roles,
      value.app_roles,
      value.app_role,
      value.platform_role,
      value.platform_roles,
    ]);

  const flatRoles = [...sources, ...nestedSources].flatMap(extractRoleList);
  return flatRoles;
};

export const parseJwtPayload = (token?: string): Record<string, unknown> | null => {
  if (!token) return null;

  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const decoded = atob(normalized);
    const json = decodeURIComponent(
      decoded
        .split("")
        .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
        .join("")
    );
    return JSON.parse(json) as Record<string, unknown>;
  } catch (error) {
    console.warn("Failed to parse auth token payload:", error);
    return null;
  }
};
